import React, { lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { Button } from 'react-bootstrap';
import Skeleton from 'wsm-skeleton';
import {
	useLabels,
	usePrefs,
	useRequestData,
	useFlags
} from 'ws-scripts/modules/common';
import { addImageParams } from '../../../utilities/images';
import { setModalSettings } from '../../../actions/modalSettings';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import VehicleImage from './VehicleImage';
import Callout from '../Callout';
import './Slick.scss';
import './MediaContainer.scss';
import { selectIsTabbedLayout } from '../../../features/layoutSlice';

const LazyVehicleImageCarousel = lazy(() => import('./VehicleImageCarousel'));

const Media = ({
	enableMediaCarousel,
	vehicle,
	accountPhone,
	loadingDelayed,
	usePlaceholderImage,
	willRenderBlankPlaceholderImage,
	hasCarouselImages
}) => {
	const dispatch = useDispatch();
	const flags = useFlags();
	const labels = useLabels();
	const { deviceType, widgetName, windowId } = useRequestData();
	const {
		showVideoModalButton,
		openVideoModalLabel,
		restrictInsertLocationsForVehicles
	} = usePrefs();
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehicleMediaLocation = getVehicleLocationName(
		'vehicle-media',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const { videos = [], uuid, title, link, images = [] } = vehicle;

	const phone = accountPhone;

	let videoButton = null;
	if (showVideoModalButton === 'true' && videos.length > 0) {
		videoButton = (
			<Button
				bsSize={deviceType === 'DESKTOP' ? 'xsmall' : null}
				className={`open-media-modal-btn ${
					deviceType === 'MOBILE' ? 'text-nowrap' : ''
				}`}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Video button',
						result: 'Video modal displayed'
					});

					dispatch(
						setModalSettings({
							isModalOpen: true,
							videos,
							uuid,
							title,
							link,
							phone
						})
					);
				}}
			>
				<i className="ddc-icon ddc-icon-play2 mr-2" />
				<span className="text-link text-link-muted">
					{labels.get(openVideoModalLabel)}
				</span>
			</Button>
		);
	}

	// Return false if _toggleMediaCarousel='false' added into URL param
	const showCarousel = flags['srp-use-media-carousel'] && enableMediaCarousel;
	const placeholderImageSrc =
		usePlaceholderImage && images[0]?.uri
			? addImageParams(images[0].uri)
			: null;

	// add container styling for if carousel is enabled and we have at least one vehicle with multiple images
	const addCarouselContainerStyle =
		showCarousel && hasCarouselImages
			? 'vehicle-card-media-container-carousel'
			: 'vehicle-card-media-container-no-carousel';

	const vehicleImage = (
		<div className={addCarouselContainerStyle}>
			<VehicleImage
				image={images[0]}
				imageIndex={0}
				link={vehicle.link}
				uuid={vehicle.uuid}
				isLoaded={willRenderBlankPlaceholderImage}
			/>
		</div>
	);
	let vehicleMedia = vehicleImage;

	if (!loadingDelayed) {
		// if content is a carousel with more than 1 image and useMediaCarousel FF is true
		if (showCarousel && images.length > 1) {
			vehicleMedia = (
				<div className={addCarouselContainerStyle}>
					<Suspense fallback={vehicleImage}>
						<Skeleton
							skeletonClasses="mb-4 vehicle-image-skeleton"
							aspectRatio="4by3"
							placeholderImage={placeholderImageSrc}
						/>
						<LazyVehicleImageCarousel
							images={images}
							link={vehicle.link}
							uuid={vehicle.uuid}
							videoButton={videoButton}
						/>
					</Suspense>
				</div>
			);
		}
	}
	return (
		<>
			{vehicleMedia}
			{videoButton}
			{isTabbedLayout && (
				<ul
					className="list-unstyled d-flex flex-column vehicle-card-media align-items-center hidden"
					data-location={vehicleMediaLocation}
				/>
			)}
			{!isTabbedLayout && (
				<Callout callouts={vehicle.callout} vehicle={vehicle} />
			)}
		</>
	);
};

Media.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	hasCarouselImages: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		images: PropTypes.arrayOf(PropTypes.shape({})),
		uuid: PropTypes.string,
		link: PropTypes.string,
		title: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		phone: PropTypes.string,
		callout: PropTypes.arrayOf(PropTypes.shape({})),
		videos: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	accountPhone: PropTypes.string.isRequired,
	loadingDelayed: PropTypes.bool,
	usePlaceholderImage: PropTypes.bool,
	willRenderBlankPlaceholderImage: PropTypes.bool.isRequired
};

Media.defaultProps = {
	loadingDelayed: false
};

export default Media;
